/**
 * Trustpilot Error Mapping + Secret Sanitization (Story 23.3 AC #5, #7)
 *
 * Trustpilot does NOT publish structured error codes beyond the HTTP status
 * code per `developers.trustpilot.com/faq/` verified 2026-05-16, so the
 * parser is purely HTTP-status-first (mirrors Ahrefs 20.1 + Semrush 20.4 +
 * Moz 20.7 + CallRail 22.1).
 *
 * **Round 3 Red Team Attack #1 + #2 — secret-leak defense.** The
 * `sanitizeTrustpilotErrorMessage` helper redacts THREE secret forms:
 *  - Bearer access tokens (`Authorization: Bearer <accessToken>`)
 *  - API Keys (in `apikey: <KEY>` header, query param `?apikey=`, or any
 *    plaintext occurrence)
 *  - API Secrets (rarer — only appears in the Basic-encoded token-exchange
 *    body; we redact anyway as defense-in-depth)
 *  - base64-encoded `apiKey:apiSecret` form (Basic auth header value)
 *  - URL-encoded variants of all of the above
 *
 * The redacted plaintext forms are in scope for the entire `fetchMetrics`
 * lifetime — defensive against accidental log-leak. Build-time fuzz tests
 * in `errors.test.ts` assert pathological inputs (URL-encoded, base64-
 * encoded, etc.) are caught.
 */
import type { AdapterError, AdapterErrorCode } from "../adapter";

import { sanitizeErrorMessage } from "../safe-fetch";

/**
 * Trustpilot-specific error envelope (best-effort — Trustpilot's docs don't
 * lock down the shape). When present, the `message` field is surfaced to
 * the user-facing error copy.
 */
export interface TrustpilotApiError {
  readonly status: number;
  readonly message?: string;
  readonly fault?: { readonly faultstring?: string };
}

/**
 * Custom error class for Trustpilot API call failures.
 * Mirrors Ahrefs/Semrush/Moz/CallRail's `*ApiCallError` pattern so callers
 * can `instanceof` discriminate.
 */
export class TrustpilotApiCallError extends Error {
  readonly code: AdapterErrorCode;
  readonly providerStatus?: number;
  readonly retryable: boolean;
  readonly retryAfterMs?: number;

  constructor(
    message: string,
    code: AdapterErrorCode,
    providerStatus?: number,
    retryable: boolean = false,
    retryAfterMs?: number
  ) {
    super(message);
    this.name = "TrustpilotApiCallError";
    this.code = code;
    this.providerStatus = providerStatus;
    this.retryable = retryable;
    this.retryAfterMs = retryAfterMs;
  }
}

/**
 * Default Retry-After cooldown when Trustpilot returns 429 without a
 * `Retry-After` header. Per `developers.trustpilot.com/rate-limiting/`
 * verified 2026-05-16 — the docs are silent on Retry-After. We pick 60s
 * because Trustpilot's 833/5min = ~2.78/sec; 60 seconds drops effective
 * rate to ~1/min and lets the bucket refill safely.
 */
export const TRUSTPILOT_429_COOLDOWN_MS = 60_000;

/**
 * Parse an `AdapterError` from a raw Trustpilot Response per AC #7:
 *
 *  - 400 / 422 → `invalid_request` (mapped to user-facing "request
 *    validation failed"; surface response body's `message` field if present)
 *  - 401 → `token_expired` (cron triggers `recoveryRefresh`)
 *  - 403 → `permission_denied` (plan downgrade OR private-tier API on
 *    free-tier credentials — when body mentions `"subscription"` or
 *    `"plan"`, surface "Upgrade your Trustpilot plan")
 *  - 404 → `account_not_found` (rare since helpers hardcode paths — usually
 *    means `businessUnitId` not in the discovered allowlist; the route
 *    handler cross-checks `metadata.allowedBusinessUnitIds[]`)
 *  - 429 → `rate_limited` retryable, 60s cooldown (AC #7 — Trustpilot has
 *    no documented Retry-After header)
 *  - 5xx → `provider_unavailable` retryable
 */
export function parseTrustpilotApiError(
  status: number,
  body?: string
): { code: AdapterErrorCode; retryable: boolean; retryAfterMs?: number; message?: string } {
  if (status === 400 || status === 422) {
    return {
      code: "invalid_request",
      retryable: false,
      message: extractMessage(body),
    };
  }
  if (status === 401) {
    return { code: "token_expired", retryable: false };
  }
  if (status === 403) {
    const msg = extractMessage(body);
    const planMention =
      msg && /(subscription|plan|tier|upgrade)/i.test(msg)
        ? " (upgrade your Trustpilot plan to access this endpoint)"
        : "";
    return {
      code: "permission_denied",
      retryable: false,
      message: msg ? `${msg}${planMention}` : undefined,
    };
  }
  if (status === 404) {
    return { code: "account_not_found", retryable: false };
  }
  if (status === 429) {
    return {
      code: "rate_limited",
      retryable: true,
      retryAfterMs: TRUSTPILOT_429_COOLDOWN_MS,
    };
  }
  if (status >= 500) {
    return { code: "provider_unavailable", retryable: true };
  }
  return { code: "invalid_response", retryable: false };
}

function extractMessage(body: string | undefined): string | undefined {
  if (!body) return undefined;
  try {
    const parsed = JSON.parse(body);
    if (parsed && typeof parsed === "object") {
      const obj = parsed as Record<string, unknown>;
      if (typeof obj.message === "string") return obj.message;
      if (typeof obj.fault === "object" && obj.fault !== null) {
        const fault = obj.fault as Record<string, unknown>;
        if (typeof fault.faultstring === "string") return fault.faultstring;
      }
    }
  } catch {
    // Not JSON — return raw body truncated for safety
    return body.length > 200 ? `${body.slice(0, 200)}…` : body;
  }
  return undefined;
}

/**
 * Sanitize a Trustpilot error message — redact ALL THREE secret forms
 * (Bearer access token, API Key, API Secret) AND URL-encoded variants AND
 * the base64-encoded `apiKey:apiSecret` Basic auth form.
 *
 * Per Round 3 Red Team Attack #1 + #2: the decrypted plaintext forms are
 * in scope for the entire `fetchMetrics` lifetime. This helper is the
 * last line of defense before an error message reaches `console.log`, a
 * Sentry breadcrumb, or a queue error payload.
 *
 * Layered redaction:
 *   1. Generic `sanitizeErrorMessage` from `../safe-fetch` (strips long
 *      base64 chunks + `Bearer XXX` + `?token=XXX` URL params).
 *   2. Targeted plaintext replacement of the THREE decrypted values
 *      (when provided).
 *   3. URL-encoded variant replacement (`%20` instead of space, etc.) —
 *      defensive against error messages that pass through a URL escape.
 *   4. Base64-encoded `apiKey:apiSecret` form replacement — defensive
 *      against error messages that quote the Basic auth value verbatim.
 *
 * Build-time fuzz test in `errors.test.ts` asserts:
 *   sanitizeTrustpilotErrorMessage(
 *     'got 401 with Authorization: Bearer abc123',
 *     'abc123',
 *     '<apikey>',
 *     '<apisecret>'
 *   ) === 'got 401 with Authorization: Bearer [redacted-trustpilot-token]'
 */
export function sanitizeTrustpilotErrorMessage(
  message: string,
  decryptedAccessToken?: string,
  decryptedApiKey?: string,
  decryptedApiSecret?: string
): string {
  // Generic first-pass redaction (long base64 + Bearer + ?token=)
  let sanitized = sanitizeErrorMessage(message);

  // Targeted plaintext + URL-encoded variant redaction
  if (decryptedAccessToken && decryptedAccessToken.length > 0) {
    sanitized = replaceAllWithVariants(
      sanitized,
      decryptedAccessToken,
      "[redacted-trustpilot-token]"
    );
  }
  if (decryptedApiKey && decryptedApiKey.length > 0) {
    sanitized = replaceAllWithVariants(
      sanitized,
      decryptedApiKey,
      "[redacted-trustpilot-api-key]"
    );
  }
  if (decryptedApiSecret && decryptedApiSecret.length > 0) {
    sanitized = replaceAllWithVariants(
      sanitized,
      decryptedApiSecret,
      "[redacted-trustpilot-api-secret]"
    );
  }

  // Base64-encoded `apiKey:apiSecret` Basic auth form
  if (
    decryptedApiKey &&
    decryptedApiSecret &&
    decryptedApiKey.length > 0 &&
    decryptedApiSecret.length > 0
  ) {
    try {
      const basicEncoded = btoa(`${decryptedApiKey}:${decryptedApiSecret}`);
      sanitized = replaceAllWithVariants(
        sanitized,
        basicEncoded,
        "[redacted-trustpilot-basic-auth]"
      );
    } catch {
      // Non-Latin-1 chars — skip; the generic long-base64 regex in step 1
      // already caught any 40+ char base64 chunks.
    }
  }

  // Belt-and-braces: any remaining literal `Bearer ` followed by a non-
  // whitespace token sequence (the generic safeFetch redactor handles
  // most of these but our custom redaction tag is more specific).
  sanitized = sanitized.replace(
    /Authorization:\s*Bearer\s+\S+/gi,
    "Authorization: Bearer [redacted-trustpilot-token]"
  );
  sanitized = sanitized.replace(
    /Authorization:\s*Basic\s+\S+/gi,
    "Authorization: Basic [redacted-trustpilot-basic-auth]"
  );
  sanitized = sanitized.replace(
    /apikey:\s*\S+/gi,
    "apikey: [redacted-trustpilot-api-key]"
  );

  return sanitized;
}

/**
 * Replace every occurrence of `needle` AND its URL-encoded variant with
 * `replacement`. URL-encoded forms (`%20` for spaces, `%2F` for slashes
 * etc.) appear when error messages travel through URL parsing layers.
 */
function replaceAllWithVariants(
  haystack: string,
  needle: string,
  replacement: string
): string {
  if (needle.length === 0) return haystack;
  const escapedNeedle = escapeRegExp(needle);
  let out = haystack.replace(new RegExp(escapedNeedle, "g"), replacement);

  // URL-encoded variant
  try {
    const encoded = encodeURIComponent(needle);
    if (encoded !== needle && encoded.length > 0) {
      out = out.replace(new RegExp(escapeRegExp(encoded), "g"), replacement);
    }
  } catch {
    // Unencodable — skip
  }

  return out;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Build an `AdapterError` from a raw Trustpilot response (used by the
 * adapter's `fetchMetrics` + `validateConnection` paths).
 */
export function buildTrustpilotAdapterError(
  status: number,
  body: string | undefined,
  context: { decryptedAccessToken?: string; decryptedApiKey?: string; decryptedApiSecret?: string }
): AdapterError {
  const parsed = parseTrustpilotApiError(status, body);
  const baseMessage =
    parsed.message ?? `HTTP ${status} from api.trustpilot.com`;
  return {
    code: parsed.code,
    message: sanitizeTrustpilotErrorMessage(
      baseMessage,
      context.decryptedAccessToken,
      context.decryptedApiKey,
      context.decryptedApiSecret
    ),
    retryable: parsed.retryable,
    retryAfterMs: parsed.retryAfterMs,
    providerStatus: status,
    providerMessage: parsed.message,
  };
}
